import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  Heading,
  NavigationalTab,
  NavigationalTabs,
  ProgressCircular,
} from '@americanexpress/dls-react';
import HotelImageCollage from '../common/HotelImageCollage';
import HotelDescription from '../common/HotelDescription';
import HotelCheckInOut from '../common/HotelCheckInOut';
import HotelFacilities from '../common/HotelFacilities';
import HotelRooms from '../common/HotelRooms';
import HotelPolicies from '../common/HotelPolicies';
import HotelImportantInformation from '../common/HotelImportantInformation';
import HotelLocationSection from '../common/HotelLocationSection';
import HotelStarRating from '../common/HotelStarRating';

const spotnanaHotelShop = require('../../mocks/spotnanaHotelShop');

const HOTEL_SECTION_ITEMS = [
  { id: 'overview', label: 'Overview' },
  { id: 'location', label: 'Location' },
  { id: 'facilities', label: 'Facilities' },
  { id: 'rooms', label: 'Rooms' },
  { id: 'policies', label: 'Policies' },
];

const getImageArea = (image = {}) => {
  const width = image?.dimensions?.width || 0;
  const height = image?.dimensions?.height || 0;
  return width * height;
};

const getHighestResolutionImage = (images = []) => images
  .filter((image) => image?.url)
  .reduce((best, current) => {
    if (!best) return current;
    return getImageArea(current) > getImageArea(best) ? current : best;
  }, null);

const MEDIUM_IMAGE_TARGET_AREA = 120000;
const getMediumResolutionImage = (images = []) => images
  .filter((image) => image?.url)
  .reduce((best, current) => {
    if (!best) return current;
    const currentDistance = Math.abs(getImageArea(current) - MEDIUM_IMAGE_TARGET_AREA);
    const bestDistance = Math.abs(getImageArea(best) - MEDIUM_IMAGE_TARGET_AREA);
    return currentDistance < bestDistance ? current : best;
  }, null);

const MEAL_TYPE_LABELS = {
  ROOM_ONLY: {
    id: 'spotnana.mealType.roomOnly',
    defaultMessage: 'Room only',
  },
  BED_AND_BREAKFAST: {
    id: 'spotnana.mealType.bedAndBreakfast',
    defaultMessage: 'Bed and breakfast',
  },
  HALF_BOARD_MODIFIED_AMERICAN_PLAN: {
    id: 'spotnana.mealType.halfBoard',
    defaultMessage: 'Half board',
  },
  FULL_BOARD: {
    id: 'spotnana.mealType.fullBoard',
    defaultMessage: 'Full board',
  },
  ALL_INCLUSIVE: {
    id: 'spotnana.mealType.allInclusive',
    defaultMessage: 'All inclusive',
  },
};

const BED_TYPE_LABELS = {
  KING: {
    id: 'spotnana.bedType.king',
    defaultMessage: 'King bed',
  },
  DOUBLE: {
    id: 'spotnana.bedType.double',
    defaultMessage: 'Double bed',
  },
  FULL: {
    id: 'spotnana.bedType.full',
    defaultMessage: 'Full bed',
  },
  DORM: {
    id: 'spotnana.bedType.dorm',
    defaultMessage: 'Dorm bed',
  },
};

const getBoardLabel = (firstRate = {}, formatMessage) => {
  const mealType = firstRate?.meals?.mealType;
  if (!mealType || mealType === 'UNKNOWN') {
    return null;
  }

  const mealTypeLabel = MEAL_TYPE_LABELS[mealType];
  if (!mealTypeLabel) {
    return null;
  }

  return formatMessage(mealTypeLabel);
};

const getBedTypeLabel = (bedType, formatMessage) => {
  if (!bedType || bedType === 'UNKNOWN_BED_TYPE') {
    return null;
  }

  const bedTypeLabel = BED_TYPE_LABELS[bedType];
  if (!bedTypeLabel) {
    return null;
  }

  return formatMessage(bedTypeLabel);
};

const extractHotelPhotos = (hotelSpec = {}) => {
  const allUrls = (hotelSpec.imageSets || [])
    .map((imageSet) => getHighestResolutionImage(imageSet?.imageGroup?.images || []))
    .map((image) => image?.url)
    .filter(Boolean);

  return [...new Set(allUrls)];
};

const getRefundLabel = (refundable) => {
  if (refundable === 'TRUE') return 'Refundable';
  if (refundable === 'FALSE') return 'Non-refundable';
  return null;
};

const buildHotelLocation = (hotelSpec = {}) => {
  const coordinates = typeof hotelSpec?.coordinates?.latitude === 'number'
    && typeof hotelSpec?.coordinates?.longitude === 'number'
    ? {
      lat: hotelSpec.coordinates.latitude,
      lng: hotelSpec.coordinates.longitude,
    }
    : null;

  return {
    coordinates,
    addressLines: [
      ...(hotelSpec?.address?.addressLines || []),
      hotelSpec?.address?.locality,
    ].filter(Boolean),
    phone: hotelSpec?.contactInfo?.phone?.[0]?.rawInput || null,
  };
};

const getDescriptionText = (hotelSpec = {}, roomRateOption = {}) => {
  const generalDescription = (hotelSpec.descriptions || []).find(
    (description) => description?.type === 'GENERAL'
  )?.value;

  if (generalDescription) return generalDescription;

  return (roomRateOption.additionalDetails || []).find(
    (detail) => detail?.additionalDetailType === 'PROPERTY_DESCRIPTION'
  )?.text || null;
};

const normalizeOverviewHtml = (html = '') => {
  const sectionPattern = /<h2[^>]*>([\s\S]*?)<\/h2>([\s\S]*?)(?=<h2[^>]*>|$)/gi;

  const normalized = html.replace(sectionPattern, (_, heading, body) => {
    const normalizedHeading = String(heading || '').trim();
    const normalizedBody = String(body || '')
      .trim()
      .replace(/\n{2,}/g, '<br /><br />')
      .replace(/\n/g, ' ');

    if (!normalizedHeading && !normalizedBody) {
      return '';
    }

    if (!normalizedBody) {
      return `<p><strong>${normalizedHeading}</strong></p>`;
    }

    return `<p><strong>${normalizedHeading}</strong><br />${normalizedBody}</p>`;
  });

  return normalized || html;
};

const uniqueValues = (values) => [...new Set(values.filter(Boolean))];

const formatAmenity = (amenity = {}) => {
  const label = typeof amenity?.type === 'string'
    ? amenity.type
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase())
    : null;

  if (!label) {
    return null;
  }

  return amenity.complimentary ? `${label} (complimentary)` : label;
};

const buildRoomDescription = (room = {}) => {
  return uniqueValues([
    room?.description,
    ...(room?.rateOptions || []).map((rate) => rate?.description),
  ]).join('<br />');
};

const buildRoomAmenities = (room = {}) => uniqueValues(
  (room?.rateOptions || []).flatMap((rate) => (rate?.amenities || []).map(formatAmenity))
);

const buildAdditionalInformation = (rooms = [], detailType) => {
  const values = rooms.flatMap((room) => (room?.rateOptions || [])
    .flatMap((rate) => (rate?.additionalDetails || [])
      .filter((detail) => detail?.additionalDetailType === detailType)
      .map((detail) => detail.text)
      .filter((text) => typeof text === 'string')));

  return uniqueValues(values)
    .map((value) => value.replace(/\n/g, '<br />'))
    .join('<br /><br />') || null;
};

const getRoomImageSets = (room = {}, fallbackImageSets = [], roomIndex = 0) => {
  if (room?.imageSets?.length > 0) {
    return room.imageSets;
  }

  const guestRoomImages = fallbackImageSets.filter((imageSet) => imageSet?.category === 'GUEST_ROOM');

  if (guestRoomImages.length === 0) {
    return [];
  }

  const imagesPerRoom = 2;
  const startIndex = (roomIndex * imagesPerRoom) % guestRoomImages.length;

  return Array.from({ length: imagesPerRoom }, (_, imageIndex) => (
    guestRoomImages[(startIndex + imageIndex) % guestRoomImages.length]
  ));
};

const buildRooms = (rooms = [], formatMessage, fallbackImageSets = []) => rooms.map((room, index) => {
  const firstRate = room?.rateOptions?.[0];
  const totalRate = firstRate?.rateInfo?.totalRate;
  const baseAmount = totalRate?.base?.convertedAmount || totalRate?.base?.amount || null;
  const currency = totalRate?.base?.convertedCurrency || totalRate?.base?.currencyCode || null;
  const publishedBaseAmount = firstRate?.rateInfo?.publishedRate?.base?.convertedAmount
    || firstRate?.rateInfo?.publishedRate?.base?.amount
    || null;
  const publishedBaseCurrency = firstRate?.rateInfo?.publishedRate?.base?.convertedCurrency
    || firstRate?.rateInfo?.publishedRate?.base?.currencyCode
    || currency;
  const freeCancellationTerm = (firstRate?.cancellationPolicy?.terms || []).find(
    (term) => term?.penalty?.penaltyAmount?.amount === 0
  );
  const bedSummary = (firstRate?.bedInfos || [])
    .map((bedInfo) => {
      const bedTypeLabel = getBedTypeLabel(bedInfo?.bedType, formatMessage);
      if (!bedTypeLabel) return null;

      return `${bedInfo.bedCount}× ${bedTypeLabel}`;
    })
    .filter(Boolean)
    .join(', ') || null;
  const boardLabel = getBoardLabel(firstRate, formatMessage);
  const maxOccupancy = (firstRate?.maxOccupancy?.numAdults || 0)
    + (firstRate?.maxOccupancy?.numChildren || 0);
  const roomPhotos = getRoomImageSets(room, fallbackImageSets, index)
    .map((imageSet) => {
      const caption = imageSet?.imageGroup?.caption;
      const selectedImage = getMediumResolutionImage(imageSet?.imageGroup?.images || []);
      if (!selectedImage?.url) return null;

      return {
        url: selectedImage.url,
        alt: caption || null,
      };
    })
    .filter(Boolean);
  const description = buildRoomDescription(room);
  const amenities = buildRoomAmenities(room);
  const pricing = baseAmount != null && currency
    ? {
      amount: baseAmount,
      currency,
      strikethroughAmount: publishedBaseAmount > 0 ? publishedBaseAmount : null,
      strikethroughCurrency: publishedBaseCurrency || null,
      boardLabel,
      refundLabel: getRefundLabel(firstRate?.cancellationPolicy?.refundable),
      freeCancellationUntil:
          freeCancellationTerm?.deadline?.deadlineAbsolute?.iso8601 || null,
      paymentLabel: firstRate?.isPrepaidRoom ? 'Prepaid' : 'Pay later',
    }
    : null;

  return {
    id: room?.roomGroupKey || index + 1,
    name:
      firstRate?.displayName
      || firstRate?.roomInfo?.typeClassDescription
      || `Room ${index + 1}`,
    description: description || null,
    amenities,
    detailsVariant: description || amenities.length > 0 ? 'modal' : 'none',
    size: null,
    maxOccupancy: maxOccupancy || null,
    bedSummary,
    photos: roomPhotos,
    pricing,
  };
});

const useSpotnanaData = () => {
  const [shopData, setShopData] = useState();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setShopData({ body: spotnanaHotelShop });
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timeout);
  }, []);

  return { shopData, error: null, isLoading };
};

const SpotnanaDetailsPage = () => {
  const { formatMessage } = useIntl();
  const { shopData, error, isLoading } = useSpotnanaData();

  if (isLoading) {
    return <ProgressCircular />;
  }

  if (error) {
    return (
      <Heading level={1} variant="sans-xlarge-book">
        Unable to load hotel details
      </Heading>
    );
  }

  const payload = shopData?.body?.data || {};
  const hotelSpec = payload?.hotelSpec || {};
  const hotelName = hotelSpec?.name;
  const starRating = hotelSpec?.starRating?.starRating || null;
  const city = hotelSpec?.address?.locality;
  const hotelMetaText = [
    city,
  ].filter(Boolean).join(' | ');

  const photos = extractHotelPhotos(hotelSpec);
  const location = buildHotelLocation(hotelSpec);
  const facilities = (hotelSpec?.amenities || [])
    .map((amenity) => amenity?.additionalInfo || amenity?.hotelAmenityType)
    .filter(Boolean);
  const roomData = payload?.rooms || [];
  const rooms = buildRooms(roomData, formatMessage, hotelSpec?.imageSets);
  const checkInDetails = buildAdditionalInformation(roomData, 'CHECK_IN_CHECK_OUT_INFORMATION');
  const amenityInformation = buildAdditionalInformation(roomData, 'AMENITY_INFORMATION');
  const roomInformation = buildAdditionalInformation(roomData, 'ROOM_INFORMATION');
  const miscellaneousInformation = buildAdditionalInformation(roomData, 'MISCELLANEOUS_INFORMATION');
  const additionalImportantInformation = buildAdditionalInformation(roomData, 'IMPORTANT_INFO');
  const description = normalizeOverviewHtml(
    getDescriptionText(hotelSpec, roomData?.[0]?.rateOptions?.[0]) || ''
  );

  const policyDescriptions = (hotelSpec?.descriptions || [])
    .filter((item) => item?.type === 'POLICIES' || item?.type === 'DEPOSIT_POLICY')
    .map((item) => ({ description: item.value?.replace(/\n/g, '<br />') }))
    .filter((item) => item.description);

  const alerts = (hotelSpec?.descriptions || [])
    .filter((item) => item?.type === 'ALERTS')
    .map((item) => item?.value?.replace(/\n/g, '<br />'))
    .filter(Boolean);

  const importantInfo = [...alerts, miscellaneousInformation, additionalImportantInformation]
    .filter(Boolean)
    .join('<br /><br />') || null;

  const checkInText = hotelSpec?.checkinTime?.iso8601 || null;
  const checkOutText = hotelSpec?.checkoutTime?.iso8601 || null;

  return (
    <>
      <Heading level={1} variant="sans-xlarge-book">{hotelName}</Heading>
      <div className="flex flex-align-center margin-1-t">
        <HotelStarRating rating={starRating} />
        {hotelMetaText ? <p className="body margin-0 margin-1-l">{hotelMetaText}</p> : null}
      </div>
      <HotelImageCollage hotelName={hotelName} photos={photos} />
      <NavigationalTabs className="margin-2-t" aria-label="Hotel detail sections">
        {HOTEL_SECTION_ITEMS.map((section, index) => (
          <NavigationalTab
            key={section.id}
            id={`tab-${section.id}`}
            href={`#${section.id}`}
            aria-current={index === 0 ? 'page' : undefined}
          >
            {section.label}
          </NavigationalTab>
        ))}
      </NavigationalTabs>

      <section id="overview" className="margin-4-t" style={{ scrollMarginTop: 'calc(64px + 1rem)' }}>
        <HotelDescription description={description} />
        <HotelCheckInOut
          checkInText={checkInText}
          checkOutText={checkOutText}
          details={checkInDetails}
        />
      </section>

      <HotelLocationSection
        hotelName={hotelName}
        coordinates={location.coordinates}
        addressLines={location.addressLines}
        phone={location.phone}
      />

      {HOTEL_SECTION_ITEMS.filter((section) => section.id !== 'overview' && section.id !== 'location').map((section) => (
        <section key={section.id} id={section.id} className="margin-4-t" style={{ scrollMarginTop: 'calc(64px + 1rem)' }}>
          <Heading level={2} variant="sans-medium-bold">{section.label}</Heading>
          {section.id === 'facilities' && (
            <>
              {amenityInformation ? (
                <div
                  className="body margin-2-t"
                  // eslint-disable-next-line react/no-danger -- content is sourced from trusted internal API
                  dangerouslySetInnerHTML={{ __html: amenityInformation }}
                />
              ) : null}
              <HotelFacilities facilities={facilities} />
            </>
          )}
          {section.id === 'rooms' && (
            <>
              {roomInformation ? (
                <div
                  className="body margin-2-t"
                  // eslint-disable-next-line react/no-danger -- content is sourced from trusted internal API
                  dangerouslySetInnerHTML={{ __html: roomInformation }}
                />
              ) : null}
              <HotelRooms rooms={rooms} />
            </>
          )}
          {section.id === 'policies' && (
            <HotelPolicies policies={policyDescriptions} />
          )}
        </section>
      ))}

      <section className="margin-4-t">
        <HotelImportantInformation content={importantInfo} />
      </section>
    </>
  );
};

export default SpotnanaDetailsPage;
